import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, ShieldCheck, ChevronDown, ChevronUp, Check, X, Plus, Trash2, Loader2 } from 'lucide-react';

const API = `${''}/api`;

const TYPE_BADGE = {
  erreur: 'bg-rose-500/15 text-rose-300',
  bug: 'bg-amber-500/15 text-amber-300',
  suggestion: 'bg-cyan-500/15 text-cyan-300',
  source: 'bg-violet-500/15 text-violet-300',
  autre: 'bg-slate-600/40 text-slate-300',
};
const STATUS_BADGE = {
  nouveau: 'bg-blue-500/15 text-blue-300',
  validé: 'bg-emerald-500/15 text-emerald-300',
  rejeté: 'bg-slate-600/40 text-slate-400',
  traité: 'bg-slate-600/40 text-slate-300',
};

export const ContributionsAdminPanel = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('feedback');

  const [feedback, setFeedback] = useState([]);
  const [counts, setCounts] = useState({});
  const [loadingFb, setLoadingFb] = useState(false);

  const [corrections, setCorrections] = useState([]);
  const [form, setForm] = useState({ correction: '', keywords: '', source: '' });

  const loadFeedback = useCallback(async () => {
    setLoadingFb(true);
    try {
      const res = await axios.get(`${API}/admin/feedback`);
      setFeedback(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) { toast.error('Erreur chargement des retours'); } finally { setLoadingFb(false); }
  }, []);

  const loadCorrections = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/corrections`); setCorrections((res.data.items || []).filter((c) => c.active !== false)); } catch (e) { /* */ }
  }, []);

  useEffect(() => { if (open) { loadFeedback(); loadCorrections(); } }, [open, loadFeedback, loadCorrections]);

  const setStatus = async (id, status) => {
    try {
      const res = await axios.post(`${API}/admin/feedback/${id}/status`, { status });
      toast.success(res.data.bonus_awarded ? 'Validé — bonus attribué au médecin' : `Marqué « ${status} »`);
      loadFeedback();
    } catch (e) { toast.error('Erreur'); }
  };

  const addCorrection = async () => {
    if (form.correction.trim().length < 5) { toast.error('Correction trop courte'); return; }
    try {
      await axios.post(`${API}/admin/corrections`, form);
      toast.success('Correction ajoutée (ancrage IA)');
      setForm({ correction: '', keywords: '', source: '' });
      loadCorrections();
    } catch (e) { toast.error('Erreur'); }
  };

  const removeCorrection = async (id) => {
    try { await axios.delete(`${API}/admin/corrections/${id}`); loadCorrections(); } catch (e) { /* */ }
  };

  return (
    <div data-testid="contributions-admin-panel" className="rounded-2xl border border-slate-700 bg-slate-900/40 p-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3" data-testid="contributions-admin-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shrink-0">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div className="text-left min-w-0">
            <h3 className="text-white font-semibold">Contributions & Amélioration</h3>
            <p className="text-xs text-slate-400 truncate">Retours des médecins + corrections validées (ancrage IA)</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4">
          <div className="flex gap-2 mb-4">
            <button onClick={() => setTab('feedback')} className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'feedback' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-300'}`} data-testid="tab-feedback">
              Retours {counts.nouveau ? <span className="ml-1 text-[10px] bg-blue-500/30 px-1.5 rounded-full">{counts.nouveau}</span> : null}
            </button>
            <button onClick={() => setTab('corrections')} className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'corrections' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-300'}`} data-testid="tab-corrections">
              Corrections IA
            </button>
          </div>

          {tab === 'feedback' && (
            loadingFb ? (
              <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>
            ) : feedback.length === 0 ? (
              <p className="text-slate-400 text-sm py-6 text-center">Aucun retour pour l'instant.</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {feedback.map((fb) => (
                  <div key={fb.id} data-testid={`feedback-item-${fb.id}`} className="bg-slate-800/40 rounded-xl p-3">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${TYPE_BADGE[fb.type] || TYPE_BADGE.autre}`}>{fb.type}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_BADGE[fb.status] || STATUS_BADGE.nouveau}`}>{fb.status}</span>
                      <span className="text-[11px] text-slate-500">{fb.pseudo}</span>
                    </div>
                    <p className="text-sm text-slate-200">{fb.message}</p>
                    {fb.context && <p className="text-[11px] text-slate-500 mt-1">Page : {fb.context}</p>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => setStatus(fb.id, 'validé')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs" data-testid={`validate-${fb.id}`}><Check className="w-3.5 h-3.5" /> Valider</button>
                      <button onClick={() => setStatus(fb.id, 'traité')} className="px-2.5 py-1 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs">Traité</button>
                      <button onClick={() => setStatus(fb.id, 'rejeté')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-400 text-xs"><X className="w-3.5 h-3.5" /> Rejeter</button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'corrections' && (
            <div className="space-y-4">
              <div className="bg-slate-800/40 rounded-xl p-3 space-y-2">
                <p className="text-xs text-slate-400">Ajouter une correction validée — elle sera injectée comme garde-fou prioritaire dans les réponses de l'IA (fiche diagnostic).</p>
                <textarea value={form.correction} onChange={(e) => setForm({ ...form, correction: e.target.value })} placeholder="Correction / règle à respecter…" rows={2} className="w-full px-3 py-2 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-200 text-sm" data-testid="correction-text" />
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="Mots-clés (séparés par des virgules)" className="flex-1 px-3 py-2 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-200 text-sm" data-testid="correction-keywords" />
                  <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Source (ex. CRAT 2024)" className="flex-1 px-3 py-2 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-200 text-sm" data-testid="correction-source" />
                </div>
                <button onClick={addCorrection} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm" data-testid="correction-add"><Plus className="w-4 h-4" /> Ajouter</button>
              </div>

              {corrections.length === 0 ? (
                <p className="text-slate-400 text-sm py-4 text-center">Aucune correction validée.</p>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {corrections.map((c) => (
                    <div key={c.id} className="flex items-start gap-2 bg-slate-800/40 rounded-lg p-3" data-testid={`correction-item-${c.id}`}>
                      <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-200">{c.correction}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{(c.keywords || []).join(', ')}{c.source ? ` · ${c.source}` : ''}</p>
                      </div>
                      <button onClick={() => removeCorrection(c.id)} className="p-1.5 text-slate-500 hover:text-rose-400" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
